/**
 * UI profilu.
 *
 * Změna oblíbeného týmu je GET Nette signal, ale nesmí se propsat
 * do browser history / adresního řádku. Proto tyto odkazy obsluhujeme
 * explicitně přes Naja s history:false.
 */
export function initProfileUI(root = document) {
    initProfileThemePayload();
    initFavoriteTeamLinks(root);
    initFanLocationSettings(root);
    initAvatarUploader(root);
}

function initProfileThemePayload() {
    if (!window.naja || window.__clasicoProfileThemePayloadReady) return;
    window.__clasicoProfileThemePayloadReady = true;

    window.naja.addEventListener('success', event => {
        const key = String(event.detail?.payload?.teamTheme || '');
        if (!['default', 'real', 'barcelona'].includes(key)) return;
        document.documentElement.dataset.teamTheme = key;
    });
}


function initFavoriteTeamLinks(root = document) {
    root.querySelectorAll('[data-profile-favorite-team]').forEach(link => {
        if (link.dataset.profileFavoriteTeamInitialized === '1') {
            return;
        }

        link.dataset.profileFavoriteTeamInitialized = '1';

        link.addEventListener('click', event => {
            event.preventDefault();
            event.stopImmediatePropagation();

            if (!window.naja || !link.href) {
                return;
            }

            window.naja.makeRequest(
                'GET',
                link.href,
                null,
                {
                    history: false,
                },
            );
        }, true);
    });
}

function initFanLocationSettings(root = document) {
    const scopes = [];
    if (root instanceof Element && root.matches('[data-fan-location-settings]')) {
        scopes.push(root);
    }
    root.querySelectorAll?.('[data-fan-location-settings]').forEach(scope => scopes.push(scope));

    scopes.forEach(scope => {
        if (scope.dataset.fanLocationInitialized === '1') return;

        let catalog = {};
        try {
            catalog = JSON.parse(scope.dataset.cityCatalog || '{}');
        } catch (error) {
            console.error('Nepodařilo se načíst číselník měst.', error);
        }

        const countrySelect = scope.querySelector('[data-fan-country-select]');
        const citySelect = scope.querySelector('[data-fan-city-select]');
        const cityHint = scope.querySelector('[data-fan-city-hint]');
        const cityFields = scope.querySelector('[data-fan-city-fields]');
        const pointFields = scope.querySelector('[data-fan-point-fields]');
        const modeCity = scope.querySelector('input[name="fan_location_mode"][value="city"]');
        const modePoint = scope.querySelector('input[name="fan_location_mode"][value="point"]');
        const latInput = scope.querySelector('[data-fan-lat]');
        const lngInput = scope.querySelector('[data-fan-lng]');
        const mapNode = scope.querySelector('#fan-location-picker');

        if (!countrySelect || !citySelect) return;

        const countryTom = countrySelect.tomselect || null;
        const cityTom = citySelect.tomselect || null;
        let map = null;
        let marker = null;

        const cityRows = Object.values(catalog)
            .map(city => ({
                id: String(city.id ?? ''),
                name: String(city.name ?? ''),
                countryCode: String(city.countryCode ?? ''),
                latitude: Number(city.latitude),
                longitude: Number(city.longitude),
            }))
            .filter(city => city.id && city.name && city.countryCode);

        const numberValue = input => {
            const value = Number.parseFloat(input?.value || '');
            return Number.isFinite(value) ? value : null;
        };

        const selectedCountry = () => String(countryTom?.getValue?.() ?? countrySelect.value ?? '');
        const selectedCityId = () => String(cityTom?.getValue?.() ?? citySelect.value ?? '');

        const setCityCoordinates = cityId => {
            const city = cityRows.find(row => row.id === String(cityId));
            if (!city) return;
            if (latInput && Number.isFinite(city.latitude)) latInput.value = city.latitude.toFixed(6);
            if (lngInput && Number.isFinite(city.longitude)) lngInput.value = city.longitude.toFixed(6);
        };

        const syncCityOptions = (preserveSelection = true) => {
            const countryCode = selectedCountry();
            const previous = preserveSelection ? selectedCityId() : '';
            const available = cityRows.filter(city => city.countryCode === countryCode);
            const validPrevious = available.some(city => city.id === previous) ? previous : '';

            if (cityTom) {
                cityTom.clear(true);
                cityTom.clearOptions();
                available.forEach(city => {
                    cityTom.addOption({ value: city.id, text: city.name });
                });
                cityTom.refreshOptions(false);
                if (validPrevious) {
                    cityTom.setValue(validPrevious, true);
                }
            } else {
                citySelect.innerHTML = '';
                const prompt = document.createElement('option');
                prompt.value = '';
                prompt.textContent = 'Vyber město';
                citySelect.appendChild(prompt);
                available.forEach(city => {
                    const option = document.createElement('option');
                    option.value = city.id;
                    option.textContent = city.name;
                    option.selected = city.id === validPrevious;
                    citySelect.appendChild(option);
                });
            }

            if (cityHint) {
                cityHint.textContent = available.length > 0
                    ? `${available.length} měst v číselníku. Piš název a TomSelect ho vyhledá.`
                    : 'Pro tuto zemi zatím nejsou předvolená města. Použij „Bod na mapě“.';
            }

            if (validPrevious) setCityCoordinates(validPrevious);
        };

        const countryCenter = () => {
            const countryCode = selectedCountry();
            const rows = cityRows.filter(city => city.countryCode === countryCode && Number.isFinite(city.latitude) && Number.isFinite(city.longitude));
            if (!rows.length) return [49.8175, 15.4730];
            const lat = rows.reduce((sum, city) => sum + city.latitude, 0) / rows.length;
            const lng = rows.reduce((sum, city) => sum + city.longitude, 0) / rows.length;
            return [lat, lng];
        };

        const ensureMap = () => {
            if (map || !mapNode || typeof window.L === 'undefined') return;

            const savedLat = numberValue(latInput);
            const savedLng = numberValue(lngInput);
            const center = savedLat !== null && savedLng !== null
                ? [savedLat, savedLng]
                : countryCenter();

            map = window.L.map(mapNode, {
                zoomControl: true,
                attributionControl: true,
            }).setView(center, savedLat !== null && savedLng !== null ? 10 : 6);

            window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 18,
                attribution: '&copy; OpenStreetMap',
            }).addTo(map);

            if (savedLat !== null && savedLng !== null) {
                marker = window.L.marker([savedLat, savedLng]).addTo(map);
            }

            map.on('click', event => {
                const { lat, lng } = event.latlng;
                if (latInput) latInput.value = lat.toFixed(6);
                if (lngInput) lngInput.value = lng.toFixed(6);

                if (marker) marker.setLatLng(event.latlng);
                else marker = window.L.marker(event.latlng).addTo(map);
            });
        };

        const syncMode = () => {
            const point = Boolean(modePoint?.checked);
            cityFields?.classList.toggle('hidden', point);
            pointFields?.classList.toggle('hidden', !point);

            if (point) {
                ensureMap();
                window.setTimeout(() => map?.invalidateSize(), 30);
            }
        };

        const handleCountryChange = () => {
            syncCityOptions(false);
            if (latInput) latInput.value = '';
            if (lngInput) lngInput.value = '';
            if (map && marker) {
                map.removeLayer(marker);
                marker = null;
            }

            if (modePoint?.checked && map) {
                const center = countryCenter();
                map.setView(center, 6);
            }
        };

        const handleCityChange = () => {
            const cityId = selectedCityId();
            if (cityId) setCityCoordinates(cityId);
        };

        if (countryTom) countryTom.on('change', handleCountryChange);
        else countrySelect.addEventListener('change', handleCountryChange);

        if (cityTom) cityTom.on('change', handleCityChange);
        else citySelect.addEventListener('change', handleCityChange);

        modeCity?.addEventListener('change', syncMode);
        modePoint?.addEventListener('change', syncMode);

        syncCityOptions(true);
        syncMode();
        scope.dataset.fanLocationInitialized = '1';
    });
}

function initAvatarUploader(root = document) {
    const scopes = [];
    if (root instanceof Element && root.matches('[data-avatar-uploader]')) scopes.push(root);
    root.querySelectorAll?.('[data-avatar-uploader]').forEach(scope => scopes.push(scope));

    scopes.forEach(scope => {
        if (scope.dataset.avatarUploaderReady === '1') return;

        const form = scope.querySelector('[data-avatar-form]');
        const input = scope.querySelector('[data-avatar-file]');
        const cropped = scope.querySelector('[data-avatar-cropped]');
        const editor = scope.querySelector('[data-avatar-editor]');
        const cropNode = scope.querySelector('[data-avatar-croppie]');
        const message = scope.querySelector('[data-avatar-message]');
        const info = scope.querySelector('[data-avatar-file-info]');
        const current = scope.querySelector('[data-avatar-current]');
        const cancel = scope.querySelector('[data-avatar-cancel]');
        const save = scope.querySelector('[data-avatar-save]');
        const remove = scope.querySelector('[data-avatar-remove]');

        if (!form || !input || !cropped || !editor || !cropNode || typeof window.Croppie !== 'function') return;

        const MAX_BYTES = 2 * 1024 * 1024;
        const MIN_SIDE = 300;
        const MAX_SIDE = 6000;
        const MAX_PIXELS = 20_000_000;
        const EDITOR_MAX_SIDE = 2200;
        const ALLOWED = new Set(['image/jpeg', 'image/png', 'image/webp']);
        let cropper = null;
        let objectUrl = null;
        let prepared = false;

        const showMessage = (text, type = 'error') => {
            if (!message) return;
            message.textContent = text;
            message.classList.remove('hidden', 'border-red-200', 'bg-red-50', 'text-red-700', 'dark:border-red-400/20', 'dark:bg-red-400/[0.06]', 'dark:text-red-300', 'border-emerald-200', 'bg-emerald-50', 'text-emerald-700', 'dark:border-emerald-400/20', 'dark:bg-emerald-400/[0.06]', 'dark:text-emerald-300');
            const classes = type === 'success'
                ? ['border-emerald-200','bg-emerald-50','text-emerald-700','dark:border-emerald-400/20','dark:bg-emerald-400/[0.06]','dark:text-emerald-300']
                : ['border-red-200','bg-red-50','text-red-700','dark:border-red-400/20','dark:bg-red-400/[0.06]','dark:text-red-300'];
            message.classList.add(...classes);
        };

        const clearMessage = () => message?.classList.add('hidden');
        const cleanupUrl = () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            objectUrl = null;
        };
        const destroy = () => {
            if (cropper) cropper.destroy();
            cropper = null;
            cleanupUrl();
            prepared = false;
            cropped.value = '';
            delete form.dataset.avatarSubmitting;
            if (save) save.disabled = false;
        };
        const reset = ({ keepMessage = false } = {}) => {
            destroy();
            input.value = '';
            editor.classList.add('hidden');
            current?.classList.remove('opacity-40');
            if (info) info.textContent = '';
            if (!keepMessage) clearMessage();
        };

        const loadImage = file => new Promise((resolve, reject) => {
            const url = URL.createObjectURL(file);
            const image = new Image();
            image.onload = () => {
                URL.revokeObjectURL(url);
                resolve(image);
            };
            image.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('invalid-image'));
            };
            image.src = url;
        });

        const makeEditorSource = async (file, image) => {
            const maxSide = Math.max(image.naturalWidth, image.naturalHeight);
            if (maxSide <= EDITOR_MAX_SIDE) return file;

            const scale = EDITOR_MAX_SIDE / maxSide;
            const width = Math.max(1, Math.round(image.naturalWidth * scale));
            const height = Math.max(1, Math.round(image.naturalHeight * scale));
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext('2d', { alpha: true });
            if (!context) return file;
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';
            context.drawImage(image, 0, 0, width, height);

            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
            canvas.width = 1;
            canvas.height = 1;
            return blob || file;
        };

        const settleMinimumZoom = async () => {
            await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
            cropper?.refresh?.();
            await new Promise(resolve => requestAnimationFrame(resolve));

            const slider = cropNode.querySelector('.cr-slider');
            if (!slider || !cropper) return;
            const min = Number(slider.min);
            const max = Number(slider.max);
            if (Number.isFinite(min)) {
                if (typeof cropper.setZoom === 'function') {
                    cropper.setZoom(min);
                } else {
                    slider.value = String(min);
                    slider.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }
            if (Number.isFinite(min) && Number.isFinite(max) && max > min) {
                slider.step = String(Math.max((max - min) / 500, 0.0001));
            } else {
                slider.step = '0.001';
            }
        };

        input.addEventListener('change', async () => {
            destroy();
            clearMessage();
            const file = input.files?.[0];
            if (!file) return;

            if (!ALLOWED.has(file.type)) {
                input.value = '';
                showMessage('Nepovolený formát. Použij JPG, PNG nebo WEBP.');
                return;
            }
            if (file.size > MAX_BYTES) {
                input.value = '';
                showMessage(`Obrázek je příliš velký (${(file.size / 1024 / 1024).toFixed(2)} MB). Maximum jsou 2 MB.`);
                return;
            }

            let image;
            try {
                image = await loadImage(file);
            } catch (_) {
                input.value = '';
                showMessage('Soubor se nepodařilo načíst jako obrázek.');
                return;
            }

            const size = { width: image.naturalWidth, height: image.naturalHeight };
            if (size.width < MIN_SIDE || size.height < MIN_SIDE) {
                input.value = '';
                showMessage(`Obrázek je příliš malý (${size.width} × ${size.height} px). Minimum je 300 × 300 px.`);
                return;
            }
            if (size.width > MAX_SIDE || size.height > MAX_SIDE || size.width * size.height > MAX_PIXELS) {
                input.value = '';
                showMessage(`Obrázek má příliš velké rozlišení (${size.width} × ${size.height} px). Maximum je 6000 px na stranu a 20 Mpx.`);
                return;
            }

            // Editor musí být viditelný dřív, než Croppie začne měřit boundary.
            // Jinak hlavně na mobilu vznikne chybný min zoom a obrázek skočí do detailu.
            editor.classList.remove('hidden');
            await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

            const availableWidth = Math.max(250, Math.floor(cropNode.parentElement?.clientWidth || 300));
            const boundarySize = Math.min(300, availableWidth);
            const viewportSize = Math.min(240, Math.max(200, boundarySize - 56));

            let editorSource;
            try {
                editorSource = await makeEditorSource(file, image);
            } catch (_) {
                editorSource = file;
            }

            objectUrl = URL.createObjectURL(editorSource);
            cropper = new window.Croppie(cropNode, {
                viewport: { width: viewportSize, height: viewportSize, type: 'square' },
                boundary: { width: boundarySize, height: boundarySize },
                enableExif: editorSource === file,
                enableOrientation: true,
                enableZoom: true,
                enforceBoundary: true,
                showZoomer: true,
                mouseWheelZoom: 'ctrl',
            });

            try {
                await cropper.bind({ url: objectUrl });
                await settleMinimumZoom();
            } catch (error) {
                reset({ keepMessage: true });
                showMessage('Editor obrázku se nepodařilo připravit. Zkus jinou fotografii.');
                return;
            }

            current?.classList.add('opacity-40');
            if (info) info.textContent = `${file.name} · ${(file.size / 1024).toFixed(0)} kB · ${size.width} × ${size.height} px`;
            prepared = true;
        });

        cancel?.addEventListener('click', () => reset());

        form.addEventListener('submit', async event => {
            if (event.submitter === remove) {
                event.preventDefault();
                if (!remove || remove.disabled) return;
                if (!window.confirm('Odebrat aktuální avatar?')) return;

                remove.disabled = true;
                clearMessage();

                try {
                    const data = new FormData(form);
                    data.set(remove.name || 'remove', remove.value || 'Odebrat avatar');
                    const response = await fetch(form.action || window.location.href, {
                        method: 'POST',
                        body: data,
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Accept': 'application/json',
                        },
                        credentials: 'same-origin',
                    });
                    const payload = await response.json().catch(() => null);
                    if (!response.ok || !payload?.ok) {
                        throw new Error(payload?.message || 'remove-failed');
                    }

                    reset();
                    if (current) {
                        current.innerHTML = '<i class="fa-duotone fa-solid fa-user text-2xl"></i>';
                    }
                    remove.remove();
                    showMessage(payload.message || 'Avatar byl odebrán.', 'success');
                } catch (error) {
                    remove.disabled = false;
                    showMessage(error?.message && error.message !== 'remove-failed' ? error.message : 'Avatar se nepodařilo odebrat. Zkus to znovu.');
                }
                return;
            }

            if (!prepared || !cropper) {
                event.preventDefault();
                showMessage('Nejdřív vyber obrázek a nastav ořez avatara.');
                return;
            }

            if (form.dataset.avatarSubmitting === '1') return;
            event.preventDefault();
            if (save) save.disabled = true;

            try {
                // PNG je jako meziformát nejspolehlivější i na mobilním Safari.
                // Server jej následně znovu zakóduje do úsporného WEBP.
                const result = await cropper.result({
                    type: 'base64',
                    size: { width: 300, height: 300 },
                    format: 'png',
                    quality: 1,
                });
                if (!result || !String(result).startsWith('data:image/png;base64,')) {
                    throw new Error('png-export');
                }
                cropped.value = result;
                form.dataset.avatarSubmitting = '1';
                form.submit();
            } catch (error) {
                if (save) save.disabled = false;
                delete form.dataset.avatarSubmitting;
                showMessage('Ořez se na tomto zařízení nepodařilo připravit. Zkus obrázek vybrat znovu; u velmi velké fotografie pomůže menší zdrojový obrázek.');
            }
        });

        scope.dataset.avatarUploaderReady = '1';
    });
}
